"use client";

import Image from "next/image";
import CardCarousel from "./CardCarousel";
import { useGqlClient } from "@/Providers/GQLProvider";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { alpha, Box, Icon } from "@mui/material";
import DOMPurify from "isomorphic-dompurify";

type Props = {
  catId: number;
  title: string;
  href: string;
  svg?: string;
  image?: string | null;
  bgColor?: string | null;
};

export default function InterestingCategoryProductsSection({ catId, title, href, svg, image, bgColor }: Props) {
  const gqlClient = useGqlClient();
  const circleSize = 50;

  const { data, isFetching, isLoading } = useQuery({
    queryKey: [gqlClient.SearchByCategoryId.name, Number(catId)],
    enabled: Boolean(catId),
    placeholderData: keepPreviousData,
    queryFn: () => gqlClient.SearchByCategoryId({ searchByCategoryId: catId as number }),
  });

  const renderIcon = () => {
    if (image) {
      return (
        <Box
          sx={{
            width: circleSize,
            height: circleSize,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
            position: "relative",
          }}
        >
          <Image
            src={image}
            alt={title}
            fill
            sizes={`${circleSize}px`}
            priority={false}
            className="category-thumb"
            style={{ objectFit: "cover" }}
          />
        </Box>
      );
    }

    return (
      <Icon
        sx={{
          width: circleSize,
          height: circleSize,
          display: "flex",
          color: (t) => t.palette.secondary.main,
          alignItems: "center",
          justifyContent: "center",
          "& svg": { width: circleSize, height: circleSize },
        }}
        {...(typeof svg === "string" ? { dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(svg) } } : {})}
      >
        {typeof svg !== "string" ? svg : null}
      </Icon>
    );
  };

  return (
    <CardCarousel
      items={data?.products as any}
      color={bgColor}
      iconImage={image}
      loading={isFetching || isLoading}
      title={title}
      icon={renderIcon()}
      href={href}
      showMore
      surface="home_category"
    />
  );
}
